import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError, downloadJournalPdf } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PaperPlaneTilt, Plus, Trophy, Heart, Moon, BowlFood, Sparkle, ArrowRight, Pulse, DownloadSimple } from "@phosphor-icons/react";

const TABS = [
  { id: "today", label: "Today" },
  { id: "symptoms", label: "Symptoms" },
  { id: "sleep", label: "Sleep" },
  { id: "milestones", label: "Milestones" },
  { id: "entries", label: "Past entries" },
];

export default function Chronic() {
  const { user, refresh } = useAuth();
  const programs = user?.programs || [];
  const paid = programs.includes("chronic365");
  const trialEnd = user?.chronic_trial_end ? new Date(user.chronic_trial_end) : null;
  const inTrial = !paid && trialEnd && trialEnd > new Date();
  const unlocked = paid || inTrial;

  const [tab, setTab] = useState("today");
  const [today, setToday] = useState(null);
  const [content, setContent] = useState("");
  const [entries, setEntries] = useState([]);
  const [symptoms, setSymptoms] = useState([]);
  const [sym, setSym] = useState({ name: "", intensity: 5, note: "" });
  const [sleeps, setSleeps] = useState([]);
  const [sleep, setSleep] = useState({ hours: 7, quality: 5, note: "" });
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    if (!unlocked) return;
    api.get("/chronic/today").then((r) => setToday(r.data)).catch(() => {});
    api.get("/chronic/journal").then((r) => setEntries(r.data)).catch(() => {});
    api.get("/chronic/symptoms").then((r) => setSymptoms(r.data)).catch(() => {});
    api.get("/chronic/sleep").then((r) => setSleeps(r.data)).catch(() => {});
  }, [unlocked]);

  const startTrial = async () => {
    setStarting(true);
    try {
      await api.post("/chronic/start-trial");
      await refresh();
      toast.success("Your 7-day trial has begun.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setStarting(false); }
  };

  if (!unlocked) {
    return (
      <div className="bg-white rounded-3xl border border-border p-10 text-center soft-shadow">
        <Pulse size={48} weight="duotone" className="mx-auto text-clay mb-4" />
        <h1 className="font-serif text-3xl mb-2">Living Well</h1>
        <p className="text-muted-foreground mb-6 max-w-md mx-auto">
          A 365-day companion for life with a chronic condition — prompts, symptom tracking,
          sleep, nutrition tips, and progress milestones.
        </p>
        <div className="flex flex-wrap gap-3 justify-center">
          <button onClick={startTrial} disabled={starting} data-testid="chronic-start-trial"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-moss text-white hover:bg-mossDark disabled:opacity-60">
            {starting ? "Starting…" : "Begin 7-day free trial"} <ArrowRight size={16} />
          </button>
          <Link to="/app/pricing" data-testid="chronic-unlock"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-border hover:bg-cream">
            Or buy lifetime · $49.99
          </Link>
        </div>
      </div>
    );
  }

  const saveEntry = async () => {
    if (!content.trim() || !today) return;
    try {
      await api.post("/chronic/journal", { content, day_number: today.day });
      setContent(""); toast.success("Entry saved.");
      api.get("/chronic/journal").then((r) => setEntries(r.data));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const logSymptom = async () => {
    if (!sym.name.trim()) return;
    try {
      await api.post("/chronic/symptom", sym);
      setSym({ name: "", intensity: 5, note: "" });
      toast.success("Symptom logged.");
      api.get("/chronic/symptoms").then((r) => setSymptoms(r.data));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const logSleep = async () => {
    try {
      await api.post("/chronic/sleep", sleep);
      setSleep({ hours: 7, quality: 5, note: "" });
      toast.success("Sleep logged.");
      api.get("/chronic/sleep").then((r) => setSleeps(r.data));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (!today) return <div className="h-32" />;
  const pct = Math.round((today.day / 365) * 100);

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs tracking-[0.3em] uppercase text-sage font-bold mb-2">
          Living Well · Day {today.day} of 365 {inTrial && !paid && <span className="text-clay">· Trial</span>}
        </p>
        <h1 className="font-serif text-4xl md:text-5xl font-medium">Listening to your body.</h1>
        <div className="relative h-3 mt-5 rounded-full bg-cream overflow-hidden max-w-md">
          <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-sage to-moss rounded-full" style={{ width: `${pct}%` }} />
        </div>
        {today.milestone && (
          <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-moss/10 text-moss">
            <Trophy size={16} weight="duotone" /> <span className="font-serif">{today.milestone.title}:</span> {today.milestone.message}
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2 border-b border-border">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`chronic-tab-${t.id}`}
                  className={`px-4 py-2 -mb-px text-sm border-b-2 transition-colors ${
                    tab === t.id ? "border-clay text-clay" : "border-transparent text-muted-foreground hover:text-charcoal"
                  }`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "today" && (
        <div className="space-y-6">
          <div className="relative bg-white rounded-3xl border border-border p-8 md:p-10 soft-shadow overflow-hidden">
            <div className="absolute -top-12 -right-12 w-56 h-56 rounded-full bg-clay/15 blur-3xl" />
            <div className="relative">
              <p className="text-xs tracking-[0.3em] uppercase text-sage font-bold mb-4 flex items-center gap-2">
                <Sparkle size={14} weight="duotone" /> Today's prompt
              </p>
              <p className="font-serif text-2xl md:text-3xl leading-snug mb-5" data-testid="chronic-prompt">{today.prompt}</p>
              <textarea value={content} onChange={(e) => setContent(e.target.value)}
                        data-testid="chronic-textarea" placeholder="Write what's true for your body today."
                        className="w-full min-h-[200px] rounded-2xl border border-border bg-sand p-5 focus:outline-none focus:ring-2 focus:ring-sage" />
              <div className="flex justify-end mt-4">
                <button onClick={saveEntry} disabled={!content.trim()} data-testid="chronic-save"
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-clay text-white hover:bg-clay/90 disabled:opacity-50">
                  <PaperPlaneTilt size={16} /> Save entry
                </button>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-3xl border border-border p-6 soft-shadow">
              <div className="flex items-center gap-2 mb-2 text-moss">
                <BowlFood size={18} weight="duotone" />
                <p className="text-xs tracking-[0.2em] uppercase font-bold">Nutrition tip</p>
              </div>
              <p className="text-charcoal leading-relaxed" data-testid="chronic-nutrition">{today.nutrition_tip}</p>
            </div>
            <div className="bg-white rounded-3xl border border-border p-6 soft-shadow">
              <div className="flex items-center gap-2 mb-2 text-clay">
                <Heart size={18} weight="duotone" />
                <p className="text-xs tracking-[0.2em] uppercase font-bold">Healthy living reminder</p>
              </div>
              <p className="text-charcoal leading-relaxed" data-testid="chronic-reminder">{today.reminder}</p>
            </div>
          </div>
          <div className="bg-cream/60 rounded-3xl border border-border p-6 italic font-serif text-lg" data-testid="chronic-quote">
            "{today.quote}"
          </div>
        </div>
      )}

      {tab === "symptoms" && (
        <div className="space-y-6">
          <div className="bg-white rounded-3xl border border-border p-6 md:p-8 soft-shadow space-y-3">
            <input value={sym.name} onChange={(e) => setSym({ ...sym, name: e.target.value })}
                   placeholder="Symptom (e.g. Joint pain, Fatigue, Brain fog)"
                   data-testid="symptom-name"
                   className="w-full px-4 py-2.5 rounded-full border border-border bg-sand focus:outline-none focus:ring-2 focus:ring-sage" />
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-sage font-bold">Intensity · {sym.intensity}/10</label>
              <input type="range" min="1" max="10" value={sym.intensity}
                     onChange={(e) => setSym({ ...sym, intensity: parseInt(e.target.value) })}
                     className="w-full mt-2 accent-clay" />
            </div>
            <textarea value={sym.note} onChange={(e) => setSym({ ...sym, note: e.target.value })}
                      placeholder="Trigger? What helped? (optional)" data-testid="symptom-note"
                      className="w-full min-h-[80px] rounded-2xl border border-border bg-sand p-4 focus:outline-none focus:ring-2 focus:ring-sage" />
            <div className="flex justify-end">
              <button onClick={logSymptom} data-testid="symptom-save"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-clay text-white hover:bg-clay/90">
                <Plus size={16} /> Log symptom
              </button>
            </div>
          </div>
          <div className="space-y-2">
            {symptoms.length === 0 ? <p className="text-muted-foreground">No symptoms logged yet.</p>
              : symptoms.map((s) => (
                <div key={s.id} className="bg-white rounded-2xl border border-border p-4">
                  <p className="font-medium">{s.name} <span className="text-xs text-muted-foreground">· {s.intensity}/10</span></p>
                  {s.note && <p className="text-sm text-muted-foreground">{s.note}</p>}
                  <p className="text-xs text-muted-foreground mt-1">{new Date(s.created_at).toLocaleString()}</p>
                </div>
              ))}
          </div>
        </div>
      )}

      {tab === "sleep" && (
        <div className="space-y-6">
          <div className="bg-white rounded-3xl border border-border p-6 md:p-8 soft-shadow space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-sage font-bold">Hours slept</label>
                <input type="number" step="0.5" min="0" max="24" value={sleep.hours}
                       onChange={(e) => setSleep({ ...sleep, hours: parseFloat(e.target.value) || 0 })}
                       data-testid="sleep-hours"
                       className="w-full mt-2 px-4 py-2.5 rounded-full border border-border bg-sand focus:outline-none focus:ring-2 focus:ring-sage" />
              </div>
              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-sage font-bold">Quality · {sleep.quality}/10</label>
                <input type="range" min="1" max="10" value={sleep.quality}
                       onChange={(e) => setSleep({ ...sleep, quality: parseInt(e.target.value) })}
                       className="w-full mt-3 accent-moss" />
              </div>
            </div>
            <textarea value={sleep.note} onChange={(e) => setSleep({ ...sleep, note: e.target.value })}
                      placeholder="How did you wake up feeling? (optional)" data-testid="sleep-note"
                      className="w-full min-h-[80px] rounded-2xl border border-border bg-sand p-4 focus:outline-none focus:ring-2 focus:ring-sage" />
            <div className="flex justify-end">
              <button onClick={logSleep} data-testid="sleep-save"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-moss text-white hover:bg-mossDark">
                <Moon size={16} weight="duotone" /> Log sleep
              </button>
            </div>
          </div>
          <div className="space-y-2">
            {sleeps.length === 0 ? <p className="text-muted-foreground">No sleep logs yet.</p>
              : sleeps.map((s) => (
                <div key={s.id} className="bg-white rounded-2xl border border-border p-4 flex items-center gap-4">
                  <Moon size={20} weight="duotone" className="text-moss" />
                  <div className="flex-1">
                    <p className="font-medium">{s.hours}h · quality {s.quality}/10</p>
                    {s.note && <p className="text-sm text-muted-foreground">{s.note}</p>}
                  </div>
                  <p className="text-xs text-muted-foreground">{new Date(s.created_at).toLocaleDateString()}</p>
                </div>
              ))}
          </div>
        </div>
      )}

      {tab === "milestones" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {(today.milestones || []).map((m) => {
            const reached = today.day >= m.day;
            return (
              <div key={m.day} data-testid={`chronic-milestone-${m.day}`}
                   className={`rounded-3xl border p-6 transition-all ${
                     reached ? "bg-moss text-white border-transparent" : "bg-white border-border"
                   }`}>
                <div className="flex items-center gap-2 mb-2">
                  <Trophy size={18} weight="duotone" className={reached ? "text-white" : "text-muted-foreground"} />
                  <span className={`text-xs tracking-[0.2em] uppercase font-bold ${reached ? "text-white/80" : "text-sage"}`}>
                    Day {m.day}
                  </span>
                </div>
                <h3 className="font-serif text-2xl mb-1">{m.title}</h3>
                <p className={`text-sm ${reached ? "text-white/85" : "text-muted-foreground"}`}>{m.message}</p>
              </div>
            );
          })}
        </div>
      )}

      {tab === "entries" && (
        <div className="space-y-3">
          {entries.length > 0 && (
            <div className="flex justify-end">
              <button onClick={() => downloadJournalPdf("chronic365")} data-testid="chronic-export-pdf"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border bg-white hover:bg-cream text-sm">
                <DownloadSimple size={14}/> Export PDF
              </button>
            </div>
          )}
          {entries.length === 0 ? <p className="text-muted-foreground">Your entries will appear here.</p>
            : entries.map((e) => (
              <div key={e.id} className="bg-white rounded-2xl border border-border p-5 soft-shadow">
                <p className="text-xs tracking-[0.2em] uppercase text-sage font-bold mb-2">
                  Day {e.day_number} · {new Date(e.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                </p>
                <p className="whitespace-pre-wrap leading-relaxed">{e.content}</p>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}
